import { useCallback, useEffect, useRef, useState } from "react";

import type { FeedbackVoteMeta } from "@jti/rest-api";
import { useFeedbackVotes } from "@jti/ui/hooks/useFeedbackVotes";
import { useUser } from "@jti/ui/hooks/useUser";

import { ContentFeedbackStatus } from "@/constants/feedback/contentFeedbackStatus";

export type ContentFeedbackReferenceType = "faq_page" | "video";

export type UseContentFeedbackStateOptions = {
  contentUrl: string;
  referenceType: ContentFeedbackReferenceType;
  referenceName: string;
};

export type UseContentFeedbackStateResult = {
  status: ContentFeedbackStatus;
  isUserLoggedIn: boolean;
  isCheckingUser: boolean;
  isInitializing: boolean;
  submitFeedback: (isHelpful: boolean) => Promise<void>;
  markSubmissionAsHandled: () => void;
};

type BackendError = {
  statusCode?: number;
  error_code?: string;
};

const DAILY_VOTING_LIMIT_REACHED_STATUS_CODE = 422;
const DAILY_VOTING_LIMIT_REACHED_ERROR_CODE = "content.feedback::daily_voting_limit_reached";

const isBackendLimitReachedError = (error: unknown): error is BackendError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "statusCode" in error &&
    error.statusCode === DAILY_VOTING_LIMIT_REACHED_STATUS_CODE &&
    "error_code" in error &&
    error.error_code === DAILY_VOTING_LIMIT_REACHED_ERROR_CODE
  );
};

export const useContentFeedbackState = ({
  contentUrl,
  referenceType,
  referenceName,
}: UseContentFeedbackStateOptions): UseContentFeedbackStateResult => {
  const { isUserLoggedIn, isCheckingUser } = useUser();
  const { submitVote, getVoteSummary } = useFeedbackVotes();
  const [status, setStatus] = useState<ContentFeedbackStatus>(ContentFeedbackStatus.Idle);
  const [isInitializing, setIsInitializing] = useState(false);
  const getVoteSummaryRef = useRef(getVoteSummary);
  const initFetchBlockedKeyRef = useRef<string | null>(null);

  const setStatusIfChanged = useCallback((nextStatus: ContentFeedbackStatus) => {
    setStatus((previousStatus) => (previousStatus === nextStatus ? previousStatus : nextStatus));
  }, []);

  useEffect(() => {
    getVoteSummaryRef.current = getVoteSummary;
  }, [getVoteSummary]);

  useEffect(() => {
    if (isCheckingUser || !isUserLoggedIn || !contentUrl) {
      setIsInitializing(false);
      return;
    }

    const initFetchKey = `${contentUrl}::${isUserLoggedIn ? "logged-in" : "logged-out"}`;

    if (initFetchBlockedKeyRef.current === initFetchKey) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    let isMounted = true;

    getVoteSummaryRef
      .current(contentUrl, referenceType, referenceName)
      .then((voteSummary: { meta?: FeedbackVoteMeta } | null) => {
        if (!isMounted) {
          return;
        }

        if (voteSummary?.meta?.can_vote === false) {
          setStatusIfChanged(
            voteSummary.meta.votes_left_today === 0
              ? ContentFeedbackStatus.LimitReached
              : ContentFeedbackStatus.Submitted
          );
        }

        setIsInitializing(false);
      })
      .catch((error: { statusCode?: number } | undefined) => {
        if (!isMounted) {
          return;
        }

        if (error?.statusCode === 401 || error?.statusCode === 403) {
          initFetchBlockedKeyRef.current = initFetchKey;
        }

        setStatusIfChanged(ContentFeedbackStatus.Idle);
        setIsInitializing(false);
      });

    return () => {
      isMounted = false;
    };
  }, [contentUrl, isCheckingUser, isUserLoggedIn, referenceName, referenceType, setStatusIfChanged]);

  const submitFeedback = useCallback(
    async (isHelpful: boolean) => {
      if (!isUserLoggedIn || isCheckingUser || !contentUrl) {
        return;
      }

      if (
        isInitializing ||
        status === ContentFeedbackStatus.Submitting ||
        status === ContentFeedbackStatus.Submitted ||
        status === ContentFeedbackStatus.LimitReached ||
        status === ContentFeedbackStatus.JustLimitReached
      ) {
        return;
      }

      setStatusIfChanged(ContentFeedbackStatus.Submitting);

      try {
        const result = await submitVote({
          vote_type: isHelpful,
          content_type: "website",
          content: {
            url: contentUrl,
            reference_type: referenceType,
            reference_name: referenceName,
          },
        });

        if (result?.meta?.can_vote === false) {
          if (result.meta.votes_left_today >= 1) {
            setStatusIfChanged(ContentFeedbackStatus.JustSubmitted);
            return;
          }

          setStatusIfChanged(ContentFeedbackStatus.JustLimitReached);
        }
      } catch (error) {
        setStatusIfChanged(
          isBackendLimitReachedError(error) ? ContentFeedbackStatus.LimitReached : ContentFeedbackStatus.Error
        );
      }
    },
    [
      contentUrl,
      isCheckingUser,
      isInitializing,
      isUserLoggedIn,
      referenceName,
      referenceType,
      setStatusIfChanged,
      status,
      submitVote,
    ]
  );

  const markSubmissionAsHandled = useCallback(() => {
    if (status === ContentFeedbackStatus.JustSubmitted) {
      setStatusIfChanged(ContentFeedbackStatus.Submitted);
    }
  }, [setStatusIfChanged, status]);

  return {
    status,
    isUserLoggedIn,
    isCheckingUser,
    isInitializing,
    submitFeedback,
    markSubmissionAsHandled,
  };
};
